<template>
    <ResourceCard
        v-if="article.data"
        class="article-card"
        document-type="article"
        :to="resolvePrismicLink(article)"
        :image="article.data.thumbnail"
        :title="article.data.title"
        :excerpt="article.data.excerpt"
        :resource-categories="(props.article.data?.resourceCategories as {resourceCategory: ResourceCategoryDocument}[]) ?? undefined"
        :category="article.data.category"
        :date="releaseDate"
        :base-data-testid="baseDataTestid"
    />
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import type {
        ArticleDocument,
        ArticleDocumentData,
        ResourceCategoryDocument,
        SuccessStoryDocument,
        SuccessStoryDocumentData,
    } from '@prismicio-types';
    import {useFormat, useLinkHelper} from '#imports';
    import ResourceCard from '~/components/ResourceCard.vue';
    import {createContentHelpers} from '~/composables/contentHelpers';

    const props = defineProps<{
        article:
            | ArticleDocument
            | FilledContentRelationshipField<'article', string, ArticleDocumentData>
            | SuccessStoryDocument
            | FilledContentRelationshipField<'successStory', string, SuccessStoryDocumentData>;
    }>();

    const {resolvePrismicLink} = useLinkHelper();
    const {getPublicationDate} = createContentHelpers();
    const {formatDate} = useFormat();
    const releaseDate = formatDate(getPublicationDate(props.article));

    const baseDataTestid = computed(() => props.article.uid ?? props.article.id);
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .article-card {
        width: auto;
        display: flex;
        flex-direction: column;
        position: relative;

        .thumbnail {
            width: 100%;
            height: 240px;
            object-fit: cover;

            @media (max-width: $mobile-breakpoint) {
                height: auto;
                max-height: 70vw;
            }
        }

        .hover-cta {
            position: absolute;
            top: 110px;
            left: 50%;
            transform: translateX(-50%) rotateZ(-8deg);
            padding: 5px 10px 3px;
            border-radius: var(--joy-core-radius-2);
            background-color: var(--joy-color-primary-50);
            z-index: 2;
            opacity: 0;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            span {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-600);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-neutral-0);
            }
        }

        &:hover {
            .hover-cta {
                opacity: 1;
            }
        }

        .top-info {
            margin-top: var(--joy-core-spacing-4);
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: var(--joy-core-spacing-2);
        }

        .categories {
            display: flex;
            gap: var(--joy-core-spacing-2);
            flex-wrap: wrap;
            flex: 1;
        }

        .date {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);

            span {
                color: var(--joy-color-neutral-40);
            }
        }

        .article-text {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .title {
            margin-top: var(--joy-core-spacing-5);
        }

        .excerpt {
            margin-top: var(--joy-core-spacing-2);

            p {
                color: var(--joy-color-neutral-40);

                @include line-clamp(2);
            }
        }
    }
</style>
